import { convertToFormat } from 'app/helpers/dateHelper';
import _ from 'lodash';
import moment from 'moment';
import { Moment } from 'moment';
import { IExtTimeOffRequest } from 'store/redux-store/placement-status/types';

/**
 * Relation Between StartDate and End Date
 * Refer: Logic from sp [amnone].[ufn_NumberOfWeeks] - (US )
 * CASE WHEN @Days % 7 < 3 THEN @Days / 7 ELSE @Days / 7 + 1 END
 *
 */

export const getExtensionEndDate = (startDateMoment: Moment, duration: number) => {
  const expectedEndDate = _.cloneDeep(startDateMoment).add(duration, 'w');
  const eDay = expectedEndDate.day();
  return expectedEndDate.day(eDay < 2 ? -1 : 6);
};

export const checkIfTimeOffExists = (
  selected: { startDate: string; endDate: string }[],
  dateRange: { startDate: string; endDate: string },
) => {
  const newS = moment(convertToFormat(dateRange.startDate, 'YYYY-MM-DD'));
  const newE = moment(convertToFormat(dateRange.endDate || '1900-01-01', 'YYYY-MM-DD'));
  return selected.some(item => {
    const s = moment(convertToFormat(item.startDate, 'YYYY-MM-DD'));
    const e = moment(convertToFormat(item.endDate || '1900-01-01', 'YYYY-MM-DD'));
    if (s.isSame(newS, 'D')) return true;
    else if (newS.isBetween(s, e, null, '[]')) {
      return true;
    } else if (newE.isBetween(s, e, null, '[]')) {
      return true;
    } else if (s.isBetween(newS, newE, null, '[]')) {
      return true;
    } else if (e.isBetween(newS, newE, null, '[]')) {
      return true;
    }
    return false;
  });
};

export const isTimeOffOutOffExtensionBound = (
  selected: { startDate: string; endDate: string }[],
  extStartMoment,
  extEndDate,
) => {
  const extEndMoment = moment(convertToFormat(extEndDate || '1900-01-01', 'YYYY-MM-DD'));
  const outOfBound = selected.filter(({ startDate, endDate }) => {
    const startMoment = moment(convertToFormat(startDate, 'YYYY-MM-DD'));
    const endMoment = moment(convertToFormat(endDate || '1900-01-01', 'YYYY-MM-DD'));
    return !(
      startMoment.isBetween(extStartMoment, extEndMoment, null, '[]') &&
      endMoment.isBetween(extStartMoment, extEndMoment, null, '[]')
    );
  });
  return { outOfBound, isOutOfBound: !!outOfBound.length };
};

export const isDatesWithInRange = (startDatePar, endDatePar, rangeStartRange, rangeEndRange) => {
  const extStartDate = moment(convertToFormat(rangeStartRange, 'YYYY-MM-DD')).toDate();
  const extEndDate = moment(convertToFormat(rangeEndRange || '1900-01-01', 'YYYY-MM-DD')).toDate();
  const startMoment = moment(convertToFormat(startDatePar, 'YYYY-MM-DD')).toDate();
  const endMoment = moment(convertToFormat(endDatePar || '1900-01-01', 'YYYY-MM-DD')).toDate();
  return startMoment >= extStartDate && endMoment <= extEndDate;
};

export const isSameDateRange = ({ startDate: s1, endDate: e1 }, { startDate: s2, endDate: e2 }) => {
  const sM1 = moment(convertToFormat(s1, 'YYYY-MM-DD'));
  const eM1 = moment(convertToFormat(e1, 'YYYY-MM-DD'));
  const sM2 = moment(convertToFormat(s2, 'YYYY-MM-DD'));
  const eM2 = moment(convertToFormat(e2, 'YYYY-MM-DD'));
  return sM1.isSame(sM2) && eM1.isSame(eM2);
};

export const getTimeOffDayCount = ({ startDate, endDate }) => {
  const startMoment = moment(convertToFormat(startDate, 'YYYY-MM-DD'));
  const endMoment = moment(convertToFormat(endDate, 'YYYY-MM-DD'));
  return endMoment.isValid() ? endMoment.diff(startMoment, 'd') + 1 : 1;
};

export const checkIfDurationIsOutOfBound = (dateRanges: { startDate; endDate }[], boundInDays: number) => {
  /** Sort the dateRanges in ascending order */
  const sorted = _.cloneDeep(dateRanges)
    // @ts-ignore
    .sort(({ startDate: a }, { startDate: b }) => new Date(a) - new Date(b));

  const outOfBoundDays = sorted.filter(({ startDate, endDate }, index, arr) => {
    const endMoment = moment(convertToFormat(endDate, 'YYYY-MM-DD'));
    const startMoment = moment(convertToFormat(startDate, 'YYYY-MM-DD'));

    /** Determine the count of days for current dateRange */
    const currentDayCount = endMoment.diff(startMoment, 'd') + 1;

    /** Determine the count of days for previous dateRange */
    let contiguousPrevDayCount = 0;
    const previousRange = arr[index - 1];
    if (!!previousRange) {
      const pStartMoment = moment(convertToFormat(previousRange.startDate, 'YYYY-MM-DD'));
      const pEndMoment = moment(convertToFormat(previousRange.endDate, 'YYYY-MM-DD'));
      const contiguity = startMoment.diff(pEndMoment, 'd');
      contiguousPrevDayCount = isNaN(contiguity) || contiguity === 1 ? pEndMoment.diff(pStartMoment, 'd') + 1 : 0;
    }

    /** Determine the count of days for next dateRange */
    let contiguousNextDayCount = 0;
    const nextRange = arr[index + 1];
    if (!!nextRange) {
      const nStartMoment = moment(convertToFormat(nextRange.startDate, 'YYYY-MM-DD'));
      const nEndMoment = moment(convertToFormat(nextRange.endDate, 'YYYY-MM-DD'));
      const contiguity = nStartMoment.diff(endMoment, 'd');
      contiguousNextDayCount = isNaN(contiguity) || contiguity === 1 ? nEndMoment.diff(nStartMoment, 'd') + 1 : 0;
    }

    return contiguousPrevDayCount + currentDayCount + contiguousNextDayCount > boundInDays;
  });

  return { outOfBoundDays, isOutOfBoundDays: !!outOfBoundDays.length };
};

/** TODO: @Rijash @Monin @Damien Remove function once service mapping is cleaned */
export const returnFalseTimeOff = (
  {
    timeOffRequestReason,
    timeOffRequestDetails,
  }: {
    timeOffRequestDetails: {
      dates: {
        startDate: string;
        endDate: string;
      };
      dailyCost: number;
      placementTimeOffPayerId: number;
      placementTimeOffPayer: string;
      facilityApprovedBy: string;
      days: number;
      hours: number;
    }[];
    timeOffRequestReason: number;
  },
  addFalseObj = true,
): IExtTimeOffRequest =>
  (!timeOffRequestReason || timeOffRequestReason === 1) && !timeOffRequestDetails.length && addFalseObj
    ? { timeOffRequestReason: 2, timeOffRequestDetails: [] }
    : {
        timeOffRequestReason,
        timeOffRequestDetails: timeOffRequestDetails.map(details => ({
          ...details,
          ...details.dates,
        })),
      };
